import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { evSearchPageClickthrough, evSwatchImpressionWrapper, nameSwatchType } from 'events/search';
import { Loader } from 'components/Loader';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import InlineProductCardAd from 'components/common/InlineProductCardAd';
import { getHeartProps } from 'helpers/HeartUtils';
import { getLowStockLabelStatus } from 'helpers/cardUtils';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import InlineAdSlot, { InlineBrandShopTheLook, InlineSponsoredAds } from 'components/search/InlineAdSlot';
import InlineBrandInfluencerShoppablePostCard from 'components/influencer/shoppablePosts/InlineBrandInfluencerShoppablePostCard';
import useRowHasBadge from 'hooks/useRowHasBadge';
import useWindowSize, { useIsMinInnerWidth } from 'hooks/useWindowSize';
import { BRAND_SHOP_THE_LOOK_CARD, BRAND_SHOP_THE_LOOK_CAROUSEL } from 'constants/influencerPages';
import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import ProductUtils from 'helpers/ProductUtils';
import { PLACEMENT } from 'apis/sponsoredAds';
import { getBadgeId } from 'helpers/BadgeUtils';
import { useSponsoredAds } from 'hooks/useSponsoredAds';

import css from 'styles/components/search/products.scss';

const MAX_ANIMATION_FRAMES = 9;

export const Products = ({
  products,
  onProductClicked,
  onProductMediaHovered,
  filters,
  heartsData = {},
  productClass,
  products: { list, allProductsCount, inlineRecos, trustedRetailers, isLoading, oosMessaging, executedSearchUrl },
  inlineBannerData,
  trackSponsoredAdImpressions,
  isCustomer,
  heartProduct,
  unHeartProduct,
  toggleHeartingLoginModal,
  makeScrollButton,
  crossSiteRecos,
  isLoadingSymphony,
  hydraBrandShopTheLookAssignment,
  brandContents,
  brandContentSlotData
}) => {
  const {
    testId,
    marketplace: {
      features: { showRatings },
      search: {
        msaMelodyImageParams,
        hasCrossSiteSearches,
        hasStyleRoomFlag,
        hasSponsoredAds,
        sponsoredAdsSlotOneRowIndex,
        sponsoredAdsSlotTwoRowIndex
      }
    }
  } = useMartyContext();
  const [animationTimerIndex, setAnimationTimerIndex] = useState(0);
  const { formattedList } = useRowHasBadge(list);
  const { page, selected, term } = filters;
  const { width: windowWidth } = useWindowSize();
  const isLargeEnoughFor4Badges = useIsMinInnerWidth(1042, 100);

  useEffect(() => {
    const { list } = products;
    // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
    // of re-renders or lazyloading, which will tick every 800ms
    let intervalId;
    const startAnimationTimer = () => {
      intervalId = setInterval(() => {
        setAnimationTimerIndex(index => {
          let newIndex = index + 1;
          // Reset the cycle
          if (newIndex > MAX_ANIMATION_FRAMES) {
            newIndex = 0;
          }
          return newIndex;
        });
      }, 800);
    };
    list.some(item => item.animationImages?.length) && startAnimationTimer(); // if we find a product that has animation images, start timer
    return () => clearInterval(intervalId);
  }, [products]);

  // Track swatch impressions
  useEffect(() => {
    const swatchImpressions = [];
    const { allProductsCount, list, trustedRetailers } = products;

    if (allProductsCount > 0) {
      const combinedProducts = hasCrossSiteSearches ? [...trustedRetailers, ...list] : [...list];

      combinedProducts.forEach(({ styleId, relatedStyles } = {}) => {
        if (relatedStyles) {
          const { visibleFabricSwatches, groupedSwatches } = ProductUtils.groupVisibleSwatches(relatedStyles, isLargeEnoughFor4Badges);
          const swatches = visibleFabricSwatches.map(({ color, styleId, swatchUrl }) => ({
            styleId,
            supplementalData: {
              swatchType: nameSwatchType(color, swatchUrl)
            }
          }));

          const groupedSwatchesProductIdentifiers = groupedSwatches.map(({ color, styleId, swatchUrl }) => ({
            styleId,
            supplementalData: {
              swatchType: nameSwatchType(color, swatchUrl)
            }
          }));

          swatchImpressions.push({
            mainStyleId: styleId,
            pageType: 'SEARCH_PAGE',
            swatches,
            groupedSwatches: groupedSwatchesProductIdentifiers
          });
        }
      });
    }

    if (swatchImpressions.length > 0) {
      track(() => [evSwatchImpressionWrapper, swatchImpressions]);
    }
  }, [hasCrossSiteSearches, products.allProductsCount, products.list, products.trustedRetailers, isLargeEnoughFor4Badges]);

  const sponsoredAds = useSponsoredAds({
    filters: { ...selected?.singleSelects, ...selected?.multiSelects },
    maxRes: 4,
    minRes: 4,
    pageType: Object.keys(filters).length > 0 ? 'Category' : 'SearchResults',
    placementId: PLACEMENT.CONTINUOUS_BLOCK_TOP,
    term
  });

  const makeCardClick = (product, index) => {
    const { heartsList = {} } = heartsData;
    const { productId, styleId, isLowStock, badges } = product;
    return (e, currentStyleId) => {
      const position = index + 1;
      const eventPayload = {
        pageNumber: page + 1,
        pageResultNumber: position,
        product,
        numberOfHearts: heartsList[styleId],
        isLowStock,
        currentStyleId,
        badgeId: getBadgeId(badges)
      };

      onProductClicked(position, productId, styleId, e);
      track(() => [evSearchPageClickthrough, eventPayload]);
    };
  };

  const makeProducts = list => {
    const { hearts } = heartsData;

    if (allProductsCount > 0) {
      const combinedProducts = hasCrossSiteSearches ? [...trustedRetailers, ...list] : [...list];

      const heartsProps = getHeartProps(
        {
          trackEvent,
          hearts,
          isCustomer,
          products,
          heartProduct,
          toggleHeartingLoginModal,
          unHeartProduct,
          isDisplayCount: true,
          hasHearting: true
        },
        {
          heartEventName: 'TE_SEARCH_PRODUCT_HEART',
          unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART'
        }
      );
      const heartsInfo = {
        heartsData: heartsProps,
        isCustomer,
        products,
        heartProduct,
        unHeartProduct,
        toggleHeartingLoginModal
      };

      const allProductCards = combinedProducts.map((v, i) => {
        const isLowStock = getLowStockLabelStatus({ ...v, hasStyleRoomFlag });
        v.isLowStock = isLowStock;
        const product = {
          ...v,
          index: i
        };
        const dataTestId = trustedRetailers[i]?.isTrustedRetailer ? 'searchResultsCrossSiteProduct' : 'searchResult';

        return (
          <ColorSwatchWrapper
            {...product}
            heartsInfo={heartsInfo}
            key={`${product.styleId}-${product.colorId}`}
            testId={testId(dataTestId)}
            animationTimerIndex={animationTimerIndex}
            msaImageParams={msaMelodyImageParams}
            onClick={makeCardClick(v, i)}
            onProductMediaHovered={onProductMediaHovered}
            link={product.productSeoUrl || product.productUrl || null}
            showRatings={showRatings}
            allowSwatchGrouping={true}
          />
        );
      });

      if (inlineBannerData?.placement) {
        const placement = parseInt(inlineBannerData.placement, 10);
        const adIndex = placement - 1;
        const canRenderAd =
          !isNaN(placement) &&
          placement > 0 &&
          allProductCards.length + 1 >= placement &&
          inlineBannerData?.heading &&
          inlineBannerData?.src &&
          inlineBannerData?.mobilesrc;

        canRenderAd && allProductCards.splice(adIndex, 0, <InlineProductCardAd key="searchInlineHero" term={term} {...inlineBannerData} />);
      }

      if (hydraBrandShopTheLookAssignment === BRAND_SHOP_THE_LOOK_CARD && brandContents?.length && brandContentSlotData?.slotPositions?.length) {
        const contentsCountToDisplay = Math.min(
          brandContentSlotData.slotPositions.length,
          brandContents.length,
          ProductUtils.getNumProductCardsForWindow(windowWidth)
        );
        for (let i = 0; i < contentsCountToDisplay; i++) {
          const cardSlotPosition = brandContentSlotData.slotPositions[i];
          const cardIndex = ProductUtils.getCardIndex(windowWidth, cardSlotPosition, 1);
          const canRenderPost = !isNaN(cardIndex) && cardIndex >= 0 && allProductCards.length > cardIndex;
          const { contentId = '' } = brandContents[i];
          canRenderPost &&
            allProductCards.splice(
              cardIndex,
              0,
              <InlineBrandInfluencerShoppablePostCard
                key={`shopTheLook-${contentId}`}
                content={brandContents[i]}
                cardIndex={i}
                slotPosition={cardSlotPosition}
                searchTerm={term}
                sourcePage={SEARCH_PAGE}
              />
            );
        }
      }

      let productCardsCount = 0;

      if (allProductsCount) {
        productCardsCount = hasCrossSiteSearches ? trustedRetailers.length + allProductCards.length : allProductCards.length;
      }

      return (
        <div id="products" data-test-id={testId('productCardContainer')} className={cn(css.products, productClass)}>
          {allProductCards}
          {executedSearchUrl && (
            <>
              <InlineAdSlot
                trackSponsoredAdImpressions={trackSponsoredAdImpressions}
                makeCardClick={makeCardClick}
                msaImageParams={msaMelodyImageParams}
                heartsInfo={heartsInfo}
                inlineRecos={inlineRecos}
                filters={filters}
                crossSiteRecos={crossSiteRecos}
                isLoadingSymphony={isLoadingSymphony}
                productCardsCount={productCardsCount}
                rowIndex={sponsoredAdsSlotOneRowIndex}
                sponsoredAds={sponsoredAds}
              />
              {hydraBrandShopTheLookAssignment === BRAND_SHOP_THE_LOOK_CAROUSEL && (
                <InlineBrandShopTheLook
                  brandContents={brandContents}
                  brandContentSlotData={brandContentSlotData}
                  filters={filters}
                  productCardsCount={productCardsCount}
                />
              )}
              {hasSponsoredAds && (
                <InlineSponsoredAds
                  rowIndex={sponsoredAdsSlotTwoRowIndex}
                  trackSponsoredAdImpressions={trackSponsoredAdImpressions}
                  makeCardClick={makeCardClick}
                  msaImageParams={msaMelodyImageParams}
                  heartsInfo={heartsInfo}
                  filters={filters}
                  term={term}
                  placement={PLACEMENT.CONTINUOUS_BLOCK_BOTTOM}
                  isLoadingSymphony={isLoadingSymphony}
                  productCardsCount={productCardsCount}
                />
              )}
            </>
          )}
        </div>
      );
    }
    return null; // no results view handled by NoSearchResults.js
  };

  // render
  return (
    <div id="searchPage" className={'searchPage'}>
      <h2>Search Results</h2>
      {oosMessaging && (
        <p className={css.oosMessaging} data-test-id={testId('oosMessage')}>
          {oosMessaging}
        </p>
      )}
      {isLoading ? <Loader /> : makeProducts(formattedList.length > 0 ? formattedList : list)}
      {makeScrollButton && makeScrollButton()}
    </div>
  );
};

Products.displayName = 'Products';

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  onProductMediaHovered: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
